// MissionTaskModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
    buildMissionTaskSummary,
    buildMissionTaskDraft,
    buildMissionTaskPayload,
    findMissionRecordType,
    getMissionBotWritableFields,
    getMissionCreateRecordFields,
    getMissionMessageDetailFields,
    getMissionRecordTypeLabel,
    getMissionUpdateModesForField,
    hasMissionValue,
    MISSION_BOT_JOB_OPTIONS,
    MISSION_TASK_HANDLED_BY,
} from './missionTaskModalUtils';
import { MISSION_DURATION_UNITS } from 'missions/scheduling/missionScheduleUtils';
import { generateKey } from 'utilities/Keys';
import {
    WORKFLOW_OUTCOME_COMPLETE,
    applyWorkflowOutgoingRoutes,
    getWorkflowOutgoingEdges,
    listWorkflowTaskOutcomeKeys,
} from 'workflows/workflowGraphUtils';

// CSS
import './MissionTaskModal.css';

// Components
import {
    buildMissionWorkspaceCommandPreview,
    buildMissionWorkspaceCommandSuggestions,
} from './buildMissionWorkspaceCommandPreview';
import { validateMissionWorkspaceCommand } from './validateMissionWorkspaceCommand';
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import Tabs from 'components/tabs/Tabs';
import Switch from 'components/switch/Switch';
import TextInput from 'components/textinput/TextInput';
import MissionTaskChecklistEditor from 'missions/taskmodal/MissionTaskChecklistEditor';
import {
    getChecklistTemplateItemLabel,
    normalizeChecklistTemplateItems,
} from 'utilities/ChecklistTemplateItems';

const toKeyString = (value) => String(value || '').trim();

const TASK_MODAL_TAB_ABOUT = 'about';
const TASK_MODAL_TAB_CHECKLIST = 'checklist';

function getDefaultTaskModalTab(mode, taskDraft) {
    if (
        mode === 'edit'
        && taskDraft?.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE
    ) {
        return TASK_MODAL_TAB_CHECKLIST;
    }
    return TASK_MODAL_TAB_ABOUT;
}


const normalizeValueForFieldType = (rawValue, fieldType) => {
    if (fieldType === 'boolean') {
        if (rawValue === true || rawValue === 'true') return true;
        if (rawValue === false || rawValue === 'false') return false;
        return '';
    }

    if (fieldType === 'number') {
        if (rawValue === '' || rawValue == null) return '';
        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : rawValue;
    }

    return rawValue;
};

/**
 * MissionTaskValueInput
 *
 * Renders the smallest field-type-aware value editor used by the Mission Bot forms.
 *
 * @param {object} field - Normalized field metadata.
 * @param {string} helperText - Optional helper text below the input.
 * @param {function} onChange - Updates the next value.
 * @param {string} placeholder - Optional input placeholder.
 * @param {unknown} value - Current controlled value.
 *
 * @returns {ReactNode} The rendered value editor.
 */
const MissionTaskValueInput = ({
    field,
    helperText = '',
    onChange,
    placeholder = '',
    value,
}) => {
    const normalizedField = field || null;

    if (!normalizedField) return null;

    if (normalizedField.type === 'singleSelect') {
        return (
            <SelectInput
                helperText={helperText}
                options={[
                    { value: '', label: 'Select a value...' },
                    ...(Array.isArray(normalizedField.enumOptions) ? normalizedField.enumOptions : []).map((option) => ({
                        value: String(option?.value ?? ''),
                        label: String(option?.label ?? option?.value ?? ''),
                    })),
                ]}
                placeholder="Select a value..."
                value={value === '' || value == null ? '' : String(value)}
                onChange={(event) => onChange?.(String(event.target.value || ''))}
            />
        );
    }

    if (normalizedField.type === 'boolean') {
        const booleanValue = value === true ? 'true' : value === false ? 'false' : '';

        return (
            <SelectInput
                helperText={helperText}
                options={[
                    { value: '', label: 'Select true or false...' },
                    { value: 'true', label: 'True' },
                    { value: 'false', label: 'False' },
                ]}
                placeholder="Select true or false..."
                value={booleanValue}
                onChange={(event) => {
                    const nextValue = String(event.target.value || '');
                    if (!nextValue) {
                        onChange?.('');
                        return;
                    }
                    onChange?.(nextValue === 'true');
                }}
            />
        );
    }

    if (normalizedField.type === 'date') {
        return (
            <TextInput
                helperText={helperText}
                placeholder={placeholder}
                type="date"
                value={value || ''}
                onChange={(event) => onChange?.(String(event.target.value || ''))}
            />
        );
    }

    if (normalizedField.type === 'dateTime') {
        return (
            <TextInput
                helperText={helperText}
                placeholder={placeholder}
                type="datetime-local"
                value={value || ''}
                onChange={(event) => onChange?.(String(event.target.value || ''))}
            />
        );
    }

    if (normalizedField.type === 'number') {
        return (
            <TextInput
                helperText={helperText}
                placeholder={placeholder}
                type="number"
                value={value === '' || value == null ? '' : String(value)}
                onChange={(event) => onChange?.(event.target.value)}
            />
        );
    }

    // ----- RENDER ----- //

    return (
        <TextInput
            helperText={helperText}
            multiline={normalizedField.type === 'longText'}
            placeholder={placeholder}
            value={value || ''}
            onChange={(event) => onChange?.(String(event.target.value || ''))}
        />
    );
};

/**
 * Builds outcomeKey → toTaskKey map from outgoing workflow edges.
 * When Complete has no edge yet, defaults to the next task in list order.
 * Kept so About/Checklist saves preserve designer-authored routes.
 *
 * @param {object[]} edges
 * @param {string} taskKey
 * @param {object[]} [availableTasks]
 * @returns {Record<string, string|null>}
 */
const buildOutcomeRoutesFromEdges = (edges, taskKey, availableTasks = []) => {
    const routes = {};
    getWorkflowOutgoingEdges(edges, taskKey).forEach((edge) => {
        const outcomeKey = String(edge?.outcomeKey || '').trim();
        if (!outcomeKey) return;
        routes[outcomeKey] = edge.toTaskKey == null ? null : String(edge.toTaskKey);
    });
    if (!(WORKFLOW_OUTCOME_COMPLETE in routes)) {
        const orderedKeys = (Array.isArray(availableTasks) ? availableTasks : [])
            .map((entry) => toKeyString(entry?.key || entry?.taskKey))
            .filter(Boolean);
        const index = orderedKeys.indexOf(toKeyString(taskKey));
        routes[WORKFLOW_OUTCOME_COMPLETE] = (
            index >= 0 && index < orderedKeys.length - 1
                ? orderedKeys[index + 1]
                : null
        );
    }
    return routes;
};

/**
 * MissionTaskModal
 *
 * Renders the first-pass Mission task editor with people assignment and Bot job flows.
 *
 * @param {array} availableFields - Anchor-record schema fields available to the task editor.
 * @param {array} availableTasks - Other workflow tasks (used to preserve default Complete routes).
 * @param {string} deleteLabel - Optional delete button label.
 * @param {array} edges - Workflow graph edges for this template (outcome routing).
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {string} mode - Create or edit mode.
 * @param {object} node - Optional anchor node used for workspace scoping.
 * @param {function} onDelete - Deletes the current task when editing.
 * @param {function} onSave - Persists the task payload; may receive `{ edges }` as a second arg.
 * @param {array} recordTypeOptions - Available record types for the Create Record Bot job.
 * @param {function} setOpen - Updates the open state.
 * @param {object} task - Optional existing task draft to edit.
 * @param {string} typeLabel - Anchor record type label for Agent command preview copy.
 * @param {string} baseKey - Optional explicit workspace scope for member pickers.
 *
 * @returns {ReactNode} The rendered modal.
 */
const MissionTaskModal = React.memo(({
    availableFields = [],
    availableTasks = [],
    deleteLabel = 'Delete Task',
    edges = [],
    isOpen = false,
    mode = 'create',
    node = null,
    onDelete,
    onSave,
    recordTypeOptions = [],
    setOpen,
    task = null,
    typeLabel = 'this record',
    baseKey = '',
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [draft, setDraft] = useState(() => buildMissionTaskDraft(task));
    const [errors, setErrors] = useState({});
    const [selectedTab, setSelectedTab] = useState(() => (
        getDefaultTaskModalTab(mode, buildMissionTaskDraft(task))
    ));
    const [outcomeRoutes, setOutcomeRoutes] = useState(() => (
        buildOutcomeRoutesFromEdges(edges, task?.key, availableTasks)
    ));

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!isOpen) return;

        const nextDraft = buildMissionTaskDraft(task);
        setDraft(nextDraft);
        setOutcomeRoutes(buildOutcomeRoutesFromEdges(edges, task?.key, availableTasks));
        setErrors({});
        setSelectedTab(getDefaultTaskModalTab(mode, nextDraft));
    }, [availableTasks, edges, isOpen, mode, task]);

    // ----- MEMOS ----- //

    const messageDetailFields = useMemo(() => {
        return getMissionMessageDetailFields(availableFields);
    }, [availableFields]);

    const writableFields = useMemo(() => {
        return getMissionBotWritableFields(availableFields);
    }, [availableFields]);

    const selectedUpdateField = useMemo(() => {
        return writableFields.find((field) => field.key === toKeyString(draft.updateRecord.fieldKey)) || null;
    }, [draft.updateRecord.fieldKey, writableFields]);

    const selectedUpdateModes = useMemo(() => {
        return getMissionUpdateModesForField(selectedUpdateField);
    }, [selectedUpdateField]);

    const selectedRecordType = useMemo(() => {
        return findMissionRecordType(recordTypeOptions, draft.createRecord.recordTypeKey);
    }, [draft.createRecord.recordTypeKey, recordTypeOptions]);

    const createRecordFields = useMemo(() => {
        return getMissionCreateRecordFields(recordTypeOptions, draft.createRecord.recordTypeKey);
    }, [draft.createRecord.recordTypeKey, recordTypeOptions]);

    const workspaceCommandSuggestions = useMemo(() => {
        return buildMissionWorkspaceCommandSuggestions(writableFields, typeLabel);
    }, [typeLabel, writableFields]);

    const workspaceCommandPreview = useMemo(() => {
        return buildMissionWorkspaceCommandPreview({
            command: draft.runWorkspaceCommand?.instruction,
            typeLabel,
        });
    }, [draft.runWorkspaceCommand?.instruction, typeLabel]);

    const isPeopleTask = draft.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE;

    const currentTaskKey = useMemo(() => (
        toKeyString(task?.key || task?.taskKey)
    ), [task?.key, task?.taskKey]);

    const tabs = useMemo(() => {
        const nextTabs = [
            {
                key: TASK_MODAL_TAB_ABOUT,
                label: 'About',
                onClick: () => setSelectedTab(TASK_MODAL_TAB_ABOUT),
            },
        ];

        if (isPeopleTask) {
            nextTabs.push({
                key: TASK_MODAL_TAB_CHECKLIST,
                label: 'Checklist',
                onClick: () => setSelectedTab(TASK_MODAL_TAB_CHECKLIST),
            });
        }

        return nextTabs;
    }, [isPeopleTask]);

    useEffect(() => {
        if (!isPeopleTask && selectedTab === TASK_MODAL_TAB_CHECKLIST) {
            setSelectedTab(TASK_MODAL_TAB_ABOUT);
        }
    }, [isPeopleTask, selectedTab]);
    const modalTitle = mode === 'edit' ? 'Edit step' : 'New step';

    // ----- UTILITIES ----- //

    const clearError = (fieldKey) => {
        setErrors((prev) => {
            if (!prev[fieldKey]) return prev;
            const next = { ...prev };
            delete next[fieldKey];
            return next;
        });
    };

    const updateDraft = (updater) => {
        setDraft((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : prev;
            return next;
        });
    };

    const updateHandledBy = (nextHandledBy) => {
        clearError('botJob');

        updateDraft((prev) => ({
            ...prev,
            estimatedDurationAmount: nextHandledBy === MISSION_TASK_HANDLED_BY.BOT ? '' : prev.estimatedDurationAmount,
            estimatedDurationUnit: nextHandledBy === MISSION_TASK_HANDLED_BY.BOT ? 'days' : prev.estimatedDurationUnit,
            handledBy: nextHandledBy,
        }));
    };

    const updateSendMessageField = (fieldKey, nextValue) => {
        clearError(`sendMessage.${fieldKey}`);

        updateDraft((prev) => ({
            ...prev,
            sendMessage: {
                ...prev.sendMessage,
                [fieldKey]: nextValue,
            },
        }));
    };

    const updateUpdateRecordField = (fieldKey, nextValue) => {
        clearError(`updateRecord.${fieldKey}`);

        updateDraft((prev) => ({
            ...prev,
            updateRecord: {
                ...prev.updateRecord,
                [fieldKey]: nextValue,
            },
        }));
    };

    const updateCreateRecordField = (fieldKey, nextValue) => {
        clearError(`createRecord.${fieldKey}`);

        updateDraft((prev) => ({
            ...prev,
            createRecord: {
                ...prev.createRecord,
                [fieldKey]: nextValue,
            },
        }));
    };

    const toggleSendMessageDetailField = (fieldKey) => {
        updateDraft((prev) => {
            const currentKeys = Array.isArray(prev.sendMessage.includeRecordFieldKeys)
                ? prev.sendMessage.includeRecordFieldKeys
                : [];
            const fieldKeySet = new Set(currentKeys.map((entry) => toKeyString(entry)).filter(Boolean));

            if (fieldKeySet.has(fieldKey)) {
                fieldKeySet.delete(fieldKey);
            } else {
                fieldKeySet.add(fieldKey);
            }

            return {
                ...prev,
                sendMessage: {
                    ...prev.sendMessage,
                    includeRecordFieldKeys: Array.from(fieldKeySet),
                },
            };
        });
    };

    const toggleCreateRecordInitialValue = (field) => {
        updateDraft((prev) => {
            const nextInitialValues = { ...prev.createRecord.initialValues };

            if (Object.prototype.hasOwnProperty.call(nextInitialValues, field.key)) {
                delete nextInitialValues[field.key];
            } else {
                nextInitialValues[field.key] = '';
            }

            return {
                ...prev,
                createRecord: {
                    ...prev.createRecord,
                    initialValues: nextInitialValues,
                },
            };
        });
    };

    const updateCreateRecordInitialValue = (field, nextValue) => {
        updateDraft((prev) => ({
            ...prev,
            createRecord: {
                ...prev.createRecord,
                initialValues: {
                    ...prev.createRecord.initialValues,
                    [field.key]: normalizeValueForFieldType(nextValue, field.type),
                },
            },
        }));
    };

    /**
     * validateDraft
     *
     * Validates the minimum-save Mission task contract for the current editor state.
     *
     * @returns {object} A keyed error map for the current draft.
     */
    const validateDraft = () => {
        const nextErrors = {};

        if (!String(draft.title || '').trim()) {
            nextErrors.title = 'Task name is required.';
        }

        if (draft.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE) {
            // Default assignees are optional; launch preflight requires owners.
            const checklistItems = normalizeChecklistTemplateItems(draft.taskChecklistItems);
            const hasBlankChecklistItem = checklistItems.some((item) => (
                !getChecklistTemplateItemLabel(item)
            ));
            if (hasBlankChecklistItem) {
                nextErrors.taskChecklistItems = 'Give each Task Checklist item a label.';
            } else if (draft.taskChecklistRequired === true && checklistItems.length === 0) {
                nextErrors.taskChecklistItems = 'Add checklist items before marking Task Checklist required.';
            }

            return nextErrors;
        }

        if (!draft.botJob) {
            nextErrors.botJob = 'Choose a Bot job.';
            return nextErrors;
        }

        if (draft.botJob === 'sendMessage') {
            if (!String(draft.sendMessage.subject || '').trim()) {
                nextErrors['sendMessage.subject'] = 'Email subject is required.';
            }

            if (!String(draft.sendMessage.message || '').trim()) {
                nextErrors['sendMessage.message'] = 'Message is required.';
            }

            return nextErrors;
        }

        if (draft.botJob === 'updateRecord') {
            if (!selectedUpdateField) {
                nextErrors['updateRecord.fieldKey'] = 'Choose a field to update.';
                return nextErrors;
            }

            if (!draft.updateRecord.mode) {
                nextErrors['updateRecord.mode'] = 'Choose how Bot should set this field.';
                return nextErrors;
            }

            if (draft.updateRecord.mode === 'setFixedValue' && !hasMissionValue(draft.updateRecord.value, selectedUpdateField.type)) {
                nextErrors['updateRecord.value'] = 'Provide a value for the selected field.';
            }

            return nextErrors;
        }

        if (draft.botJob === 'createRecord') {
            if (!selectedRecordType) {
                nextErrors['createRecord.recordTypeKey'] = 'Choose a record type to create.';
            }
            if (!String(draft.createRecord.title || '').trim()) {
                nextErrors['createRecord.title'] = 'Record title is required.';
            }
            return nextErrors;
        }

        if (draft.botJob === 'runWorkspaceCommand') {
            const commandValidation = validateMissionWorkspaceCommand(draft.runWorkspaceCommand?.instruction);
            if (!commandValidation.ok) {
                nextErrors['runWorkspaceCommand.instruction'] = commandValidation.message;
            }

            return nextErrors;
        }

        return nextErrors;
    };

    // ----- HANDLERS ----- //

    /**
     * handleSave
     *
     * Validates and persists the Mission task draft through the provided callback.
     */

    const handleSave = () => {
        const nextErrors = validateDraft();
        setErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            const errorKeys = Object.keys(nextErrors);
            if (errorKeys.some((key) => key === 'title')) {
                setSelectedTab(TASK_MODAL_TAB_ABOUT);
            } else if (errorKeys.some((key) => key === 'taskChecklistItems')) {
                setSelectedTab(TASK_MODAL_TAB_CHECKLIST);
            } else if (errorKeys.some((key) => key.startsWith('sendMessage.') || key.startsWith('updateRecord.') || key.startsWith('createRecord.') || key.startsWith('runWorkspaceCommand.') || key === 'botJob')) {
                setSelectedTab(TASK_MODAL_TAB_ABOUT);
            }
            return;
        }

        const taskKey = currentTaskKey || `task_${generateKey()}`;
        const payload = {
            ...buildMissionTaskPayload({
                ...draft,
                runWorkspaceCommand: {
                    ...draft.runWorkspaceCommand,
                    previewSummary: draft.botJob === 'runWorkspaceCommand' ? workspaceCommandPreview : '',
                },
            }, task),
            key: taskKey,
        };

        const routeEntries = listWorkflowTaskOutcomeKeys(payload).map((outcomeKey) => ({
            outcomeKey,
            toTaskKey: Object.prototype.hasOwnProperty.call(outcomeRoutes, outcomeKey)
                ? outcomeRoutes[outcomeKey]
                : null,
        }));

        onSave?.(payload, {
            edges: applyWorkflowOutgoingRoutes(edges, taskKey, routeEntries),
        });
        setOpen?.(false);
    };

    if (!isOpen) return null;

    // ----- RENDER ----- //

    return (
        <>
            <Modal
                height="auto"
                isOpen={isOpen}
                minHeight="770px"
                maxHeight="90vh"
                title={modalTitle}
                width={isMobile ? "100%" : 620}
                onClose={() => setOpen?.(false)}
            >
                <div className="mission-task-modal-container">
                    <Tabs
                        selectedKey={selectedTab}
                        tabs={tabs}
                    />

                    <ModalForm gap="20px">
                        {selectedTab === TASK_MODAL_TAB_ABOUT ? (
                            <>
                                <ModalField>
                                    <ModalLabel label="Title" />
                                    <TextInput
                                        autoFocus
                                        autoSelect
                                        error={Boolean(errors.title)}
                                        helperText={errors.title || ''}
                                        placeholder='e.g. "Review Handbook With Employee"'
                                        value={draft.title}
                                        width="100%"
                                        onChange={(event) => {
                                            clearError('title');
                                            updateDraft((prev) => ({
                                                ...prev,
                                                title: String(event.target.value || ''),
                                            }));
                                        }}
                                    />
                                </ModalField>

                                {/* FIELD BLOCK FULL */}
                                <div className="mission-task-modal-field-block-full">
                                    <ModalField>
                                        <ModalLabel label="Description" />
                                        <ModalHint hint="Optional context or guidance for how this task should be completed." />
                                        <TextInput
                                            style={{
                                                minHeight: '47px',
                                            }}
                                            expandable
                                            multiline
                                            placeholder='e.g. Walk through key policies and answer questions.'
                                            value={draft.description}
                                            onChange={(event) => {
                                                updateDraft((prev) => ({
                                                    ...prev,
                                                    description: String(event.target.value || ''),
                                                }));
                                            }}
                                        />
                                    </ModalField>
                                </div>

                                {isPeopleTask ? (
                                    <ModalField>
                                        <ModalLabel label="Estimated Duration" />
                                        <ModalHint hint="Used to project when this mission may finish. Leave blank if unsure." />

                                        {/* DURATION ROW */}
                                        <div className="mission-task-modal-duration-row">
                                            <TextInput
                                                placeholder="e.g. 2"
                                                type="number"
                                                value={draft.estimatedDurationAmount === '' || draft.estimatedDurationAmount == null
                                                    ? ''
                                                    : String(draft.estimatedDurationAmount)}
                                                width="100%"
                                                onChange={(event) => {
                                                    updateDraft((prev) => ({
                                                        ...prev,
                                                        estimatedDurationAmount: String(event.target.value || ''),
                                                    }));
                                                }}
                                            />
                                            <SelectInput
                                                options={(Array.isArray(MISSION_DURATION_UNITS) ? MISSION_DURATION_UNITS : []).map((unit) => ({
                                                    value: String(unit?.value ?? ''),
                                                    label: String(unit?.label ?? unit?.value ?? ''),
                                                }))}
                                                placeholder="Days"
                                                value={draft.estimatedDurationUnit || 'days'}
                                                onChange={(event) => {
                                                    updateDraft((prev) => ({
                                                        ...prev,
                                                        estimatedDurationUnit: String(event.target.value || 'days'),
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </ModalField>
                                ) : null}

                                <ModalField>
                                    <div className="mission-task-modal-section-header">
                                        <div className="mission-task-modal-section-header-left">
                                            <ModalLabel label="Automate" />
                                        </div>
                                        <div className="mission-task-modal-section-header-right">
                                            <Switch
                                                checked={draft.handledBy === MISSION_TASK_HANDLED_BY.BOT}
                                                onChange={(nextChecked) => updateHandledBy(
                                                    nextChecked
                                                        ? MISSION_TASK_HANDLED_BY.BOT
                                                        : MISSION_TASK_HANDLED_BY.PEOPLE
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {draft.handledBy === MISSION_TASK_HANDLED_BY.BOT ? (
                                        <div className="mission-task-modal-bot-card">
                                            <div className="mission-task-modal-field-block">
                                                <ModalHint hint="Automations run when this task becomes ready on the playbook path." />

                                                <div className="mission-task-modal-field-list">
                                                    {MISSION_BOT_JOB_OPTIONS.map((option) => {
                                                        const isSelected = draft.botJob === option.value;

                                                        return (
                                                            <div
                                                                className="mission-task-modal-field-row"
                                                                style={{
                                                                    borderColor: theme.outlineMuted,
                                                                }}
                                                                key={option.value}
                                                                onClick={() => {
                                                                    clearError('botJob');
                                                                    updateDraft((prev) => ({
                                                                        ...prev,
                                                                        botJob: option.value,
                                                                    }));
                                                                }}
                                                            >
                                                                <Checkbox
                                                                    value={isSelected}
                                                                    onChange={(event) => {
                                                                        event?.stopPropagation?.();
                                                                        clearError('botJob');
                                                                        updateDraft((prev) => ({
                                                                            ...prev,
                                                                            botJob: option.value,
                                                                        }));
                                                                    }}
                                                                />

                                                                <div className="mission-task-modal-field-meta">
                                                                    <div className="mission-task-modal-field-name">
                                                                        {option.label}
                                                                    </div>
                                                                    {option.description ? (
                                                                        <div className="mission-task-modal-field-description">
                                                                            {option.description}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {errors.botJob ? (
                                                    <div className="mission-task-modal-error">
                                                        {errors.botJob}
                                                    </div>
                                                ) : null}
                                            </div>

                                            {draft.botJob === 'sendMessage' ? (
                                                <div className="mission-task-modal-job-grid">
                                                    <ModalField>
                                                        <ModalLabel label="Recipient" />
                                                        <ModalHint hint="The base member who receives this email will be selected when the Mission is launched." />
                                                    </ModalField>

                                                    <ModalField>
                                                        <ModalLabel label="Subject" />
                                                        <TextInput
                                                            error={Boolean(errors['sendMessage.subject'])}
                                                            helperText={errors['sendMessage.subject'] || ''}
                                                            placeholder="Email subject"
                                                            value={draft.sendMessage.subject}
                                                            onChange={(event) => updateSendMessageField('subject', String(event.target.value || ''))}
                                                        />
                                                    </ModalField>

                                                    <div className="mission-task-modal-field-block-full">
                                                        <ModalField>
                                                            <ModalLabel label="Message" />
                                                            <TextInput
                                                                error={Boolean(errors['sendMessage.message'])}
                                                                helperText={errors['sendMessage.message'] || ''}
                                                                multiline
                                                                placeholder="Write the message Bot should send"
                                                                value={draft.sendMessage.message}
                                                                onChange={(event) => updateSendMessageField('message', String(event.target.value || ''))}
                                                            />
                                                        </ModalField>
                                                    </div>

                                                    <div className="mission-task-modal-field-block-full">
                                                        <ModalField>
                                                            <ModalLabel label="Include Record Details" />
                                                            <ModalHint hint="Optionally include simple record details in the outgoing message." />

                                                            <div className="mission-task-modal-field-list">
                                                                {messageDetailFields.length > 0 ? messageDetailFields.map((field) => {
                                                                    const isIncluded = draft.sendMessage.includeRecordFieldKeys.includes(field.key);

                                                                    return (
                                                                        <div
                                                                            className="mission-task-modal-field-row"
                                                                            style={{
                                                                                borderColor: theme.outlineMuted,
                                                                            }}
                                                                            key={field.key}
                                                                            onClick={() => toggleSendMessageDetailField(field.key)}
                                                                        >
                                                                            <Checkbox
                                                                                value={isIncluded}
                                                                                onChange={(event) => {
                                                                                    event?.stopPropagation?.();
                                                                                    toggleSendMessageDetailField(field.key);
                                                                                }}
                                                                            />

                                                                            <div className="mission-task-modal-field-meta">
                                                                                <div className="mission-task-modal-field-name">
                                                                                    {field.label}
                                                                                </div>
                                                                                {field.description ? (
                                                                                    <div className="mission-task-modal-field-description">
                                                                                        {field.description}
                                                                                    </div>
                                                                                ) : null}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                }) : (
                                                                    <div className="mission-task-modal-empty">
                                                                        No simple record fields are available to include yet.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </ModalField>
                                                    </div>
                                                </div>
                                            ) : null}

                                            {draft.botJob === 'updateRecord' ? (
                                                <div className="mission-task-modal-job-grid">
                                                    <ModalField>
                                                        <ModalLabel label="Field" />
                                                        <SelectInput
                                                            error={Boolean(errors['updateRecord.fieldKey'])}
                                                            helperText={errors['updateRecord.fieldKey'] || ''}
                                                            options={[
                                                                { value: '', label: 'Choose a field to update...' },
                                                                ...writableFields.map((field) => ({
                                                                    value: String(field.key || ''),
                                                                    label: String(field.label || field.key || ''),
                                                                })),
                                                            ]}
                                                            placeholder="Choose a field to update..."
                                                            value={draft.updateRecord.fieldKey}
                                                            onChange={(event) => {
                                                                const nextFieldKey = String(event.target.value || '');
                                                                const nextField = writableFields.find((field) => field.key === nextFieldKey) || null;
                                                                const nextModes = getMissionUpdateModesForField(nextField);

                                                                updateDraft((prev) => ({
                                                                    ...prev,
                                                                    updateRecord: {
                                                                        fieldKey: nextFieldKey,
                                                                        mode: nextModes[0]?.value || '',
                                                                        value: '',
                                                                    },
                                                                }));

                                                                clearError('updateRecord.fieldKey');
                                                                clearError('updateRecord.mode');
                                                                clearError('updateRecord.value');
                                                            }}
                                                        />
                                                    </ModalField>

                                                    {selectedUpdateField ? (
                                                        <ModalField>
                                                            <ModalLabel label="Update Mode" />
                                                            <SelectInput
                                                                error={Boolean(errors['updateRecord.mode'])}
                                                                helperText={errors['updateRecord.mode'] || ''}
                                                                options={[
                                                                    { value: '', label: 'Choose a mode...' },
                                                                    ...selectedUpdateModes.map((option) => ({
                                                                        value: String(option?.value ?? ''),
                                                                        label: String(option?.label ?? option?.value ?? ''),
                                                                    })),
                                                                ]}
                                                                placeholder="Choose a mode..."
                                                                value={draft.updateRecord.mode}
                                                                onChange={(event) => updateUpdateRecordField('mode', String(event.target.value || ''))}
                                                            />
                                                        </ModalField>
                                                    ) : null}

                                                    {draft.updateRecord.mode === 'setFixedValue' && selectedUpdateField ? (
                                                        <div className="mission-task-modal-field-block-full">
                                                            <ModalField>
                                                                <ModalLabel label="Value" />
                                                                <MissionTaskValueInput
                                                                    field={selectedUpdateField}
                                                                    helperText={errors['updateRecord.value'] || ''}
                                                                    placeholder="Enter a value"
                                                                    value={draft.updateRecord.value}
                                                                    onChange={(nextValue) => updateUpdateRecordField('value', normalizeValueForFieldType(nextValue, selectedUpdateField.type))}
                                                                />
                                                            </ModalField>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ) : null}

                                            {draft.botJob === 'createRecord' ? (
                                                <div className="mission-task-modal-job-grid">
                                                    <ModalField>
                                                        <ModalLabel label="Record Type" />
                                                        <SelectInput
                                                            error={Boolean(errors['createRecord.recordTypeKey'])}
                                                            helperText={errors['createRecord.recordTypeKey'] || ''}
                                                            options={[
                                                                { value: '', label: 'Choose a record type...' },
                                                                ...(Array.isArray(recordTypeOptions) ? recordTypeOptions : []).map((option) => {
                                                                    const optionKey = toKeyString(option?.key || option?.value || option?.typeKey);
                                                                    const optionLabel = getMissionRecordTypeLabel(option) || optionKey;
                                                                    return {
                                                                        value: optionKey,
                                                                        label: optionLabel,
                                                                    };
                                                                }).filter((option) => option.value),
                                                            ]}
                                                            placeholder="Choose a record type..."
                                                            value={draft.createRecord.recordTypeKey}
                                                            onChange={(event) => {
                                                                const nextRecordTypeKey = String(event.target.value || '');
                                                                updateDraft((prev) => ({
                                                                    ...prev,
                                                                    createRecord: {
                                                                        ...prev.createRecord,
                                                                        initialValues: {},
                                                                        recordTypeKey: nextRecordTypeKey,
                                                                    },
                                                                }));
                                                                clearError('createRecord.recordTypeKey');
                                                            }}
                                                        />
                                                    </ModalField>

                                                    <ModalField>
                                                        <ModalLabel label="Title" />
                                                        <TextInput
                                                            error={Boolean(errors['createRecord.title'])}
                                                            helperText={errors['createRecord.title'] || ''}
                                                            placeholder="New record title"
                                                            value={draft.createRecord.title}
                                                            onChange={(event) => updateCreateRecordField('title', String(event.target.value || ''))}
                                                        />
                                                    </ModalField>

                                                    <div className="mission-task-modal-field-block-full">
                                                        <ModalField>
                                                            <ModalLabel label="Initial Values" />
                                                            <ModalHint hint="Only simple fields are supported in the first release." />

                                                            <div className="mission-task-modal-field-list">
                                                                {selectedRecordType ? createRecordFields.length > 0 ? createRecordFields.map((field) => {
                                                                    const isIncluded = Object.prototype.hasOwnProperty.call(draft.createRecord.initialValues, field.key);
                                                                    const fieldValue = draft.createRecord.initialValues[field.key];

                                                                    return (
                                                                        <div
                                                                            className="mission-task-modal-initial-value-row"
                                                                            style={{
                                                                                borderColor: theme.outlineMuted,
                                                                            }}
                                                                            key={field.key}
                                                                        >
                                                                            <div
                                                                                className="mission-task-modal-field-row"
                                                                                onClick={() => toggleCreateRecordInitialValue(field)}
                                                                            >
                                                                                <Checkbox
                                                                                    value={isIncluded}
                                                                                    onChange={(event) => {
                                                                                        event?.stopPropagation?.();
                                                                                        toggleCreateRecordInitialValue(field);
                                                                                    }}
                                                                                />

                                                                                <div className="mission-task-modal-field-meta">
                                                                                    <div className="mission-task-modal-field-name">
                                                                                        {field.label}
                                                                                    </div>
                                                                                    {field.description ? (
                                                                                        <div className="mission-task-modal-field-description">
                                                                                            {field.description}
                                                                                        </div>
                                                                                    ) : null}
                                                                                </div>
                                                                            </div>

                                                                            {isIncluded ? (
                                                                                <div className="mission-task-modal-initial-value-editor">
                                                                                    <MissionTaskValueInput
                                                                                        field={field}
                                                                                        placeholder="Enter an initial value"
                                                                                        value={fieldValue}
                                                                                        onChange={(nextValue) => updateCreateRecordInitialValue(field, nextValue)}
                                                                                    />
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    );
                                                                }) : (
                                                                    <div className="mission-task-modal-empty">
                                                                        This record type does not expose any simple fields for initial values yet.
                                                                    </div>
                                                                ) : (
                                                                    <div className="mission-task-modal-empty">
                                                                        Choose a record type to configure initial values.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </ModalField>
                                                    </div>
                                                </div>
                                            ) : null}

                                            {draft.botJob === 'runWorkspaceCommand' ? (
                                                <div className="mission-task-modal-job-grid">
                                                    <ModalField>
                                                        <ModalLabel label="Add a command" />
                                                        <ModalHint hint="Describe what the Agent should do on this record when the task runs. For simple field changes, Update Record is easier to preview." />

                                                        <textarea
                                                            className="mission-task-modal-command-input"
                                                            style={{
                                                                borderColor: errors['runWorkspaceCommand.instruction'] ? theme.red : theme.outlineMuted,
                                                            }}
                                                            placeholder='Create an IT Setup record linked to this employee with Status set to Pending.'
                                                            rows={5}
                                                            value={draft.runWorkspaceCommand.instruction}
                                                            onChange={(event) => {
                                                                clearError('runWorkspaceCommand.instruction');
                                                                updateDraft((prev) => ({
                                                                    ...prev,
                                                                    runWorkspaceCommand: {
                                                                        ...prev.runWorkspaceCommand,
                                                                        instruction: String(event.target.value || ''),
                                                                    },
                                                                }));
                                                            }}
                                                        />

                                                        {errors['runWorkspaceCommand.instruction'] ? (
                                                            <div className="mission-task-modal-error">
                                                                {errors['runWorkspaceCommand.instruction']}
                                                            </div>
                                                        ) : null}
                                                    </ModalField>

                                                    {workspaceCommandSuggestions.length > 0 ? (
                                                        <ModalField>
                                                            <ModalLabel label="Suggestions" />

                                                            <div className="mission-task-modal-suggestion-list">
                                                                {workspaceCommandSuggestions.map((suggestion) => (
                                                                    <button
                                                                        className="mission-task-modal-suggestion-chip"
                                                                        style={{
                                                                            borderColor: theme.outlineMuted,
                                                                        }}
                                                                        key={suggestion}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            clearError('runWorkspaceCommand.instruction');
                                                                            updateDraft((prev) => ({
                                                                                ...prev,
                                                                                runWorkspaceCommand: {
                                                                                    ...prev.runWorkspaceCommand,
                                                                                    instruction: suggestion,
                                                                                },
                                                                            }));
                                                                        }}
                                                                    >
                                                                        {suggestion}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </ModalField>
                                                    ) : null}

                                                    <ModalField>
                                                        <ModalLabel label="Preview" />
                                                        <div className="mission-task-modal-preview-copy">
                                                            {workspaceCommandPreview}
                                                        </div>
                                                    </ModalField>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </ModalField>
                            </>
                        ) : null}

                        {selectedTab === TASK_MODAL_TAB_CHECKLIST && isPeopleTask ? (
                            <>
                                <MissionTaskChecklistEditor
                                    availableFields={availableFields}
                                    error={errors.taskChecklistItems || ''}
                                    items={draft.taskChecklistItems}
                                    required={draft.taskChecklistRequired === true}
                                    theme={theme}
                                    onChange={({ items, required: nextRequired }) => {
                                        clearError('taskChecklistItems');
                                        updateDraft((prev) => ({
                                            ...prev,
                                            taskChecklistItems: items,
                                            taskChecklistRequired: nextRequired === true,
                                        }));
                                    }}
                                />
                            </>
                        ) : null}
                    </ModalForm>
                </div>

                {/* ACTIONS */}
                <ModalButtons>
                    <ModalButtonSection align="START">
                        {mode === 'edit' && typeof onDelete === 'function' ? (
                            <Button
                                iconKey="delete"
                                label={deleteLabel}
                                onClick={onDelete}
                            />
                        ) : null}
                    </ModalButtonSection>

                    <ModalButtonSection align="END">
                        <Button
                            iconKey="close"
                            label="Cancel"
                            onClick={() => setOpen?.(false)}
                        />

                        <Button
                            iconKey="check"
                            label='Save'
                            onClick={handleSave}
                        />
                    </ModalButtonSection>
                </ModalButtons>
            </Modal>
        </>
    );
});

// Dev Tools
MissionTaskModal.displayName = 'MissionTaskModal';

export default MissionTaskModal;
